import apiClient from './index';
import { MAX_CASE_DOCUMENT_UPLOAD_LABEL } from '../constants/uploads';

export interface DocumentRequirement {
  id: number;
  visaType: string;
  applicationStage: string | null;
  documentCategory: 'identity' | 'supporting' | 'employment' | 'education' | 'uscis_form';
  isRequired: boolean;
  minCount: number;
  maxCount: number | null;
  description: string;
  displayOrder: number;
}

export interface RequirementStatus {
  /** application_document_requirements.id — required for uploads. */
  requirementId: number;
  category: string;
  description: string;
  isRequired: boolean;
  minCount: number;
  maxCount: number | null;
  uploadedCount: number;
  approvedCount: number;
  isMet: boolean;
  status: 'met' | 'partially_met' | 'not_met' | 'optional';
  source?: 'global' | 'conditional';
  /** Per-requirement upload guidance authored by admin in the templates panel. */
  hintText?: string | null;
  acceptedMimeTypes?: string[] | null;
  maxFileBytes?: number | null;
  slotLabels?: string[] | null;
  slots?: Array<{
    key: string;
    label: string;
    documentId: number | null;
    fileName: string | null;
    status: UserDocument['status'] | null;
  }> | null;
}

/**
 * Shape of a Document row returned by GET /applications/:id/documents.
 * Mirrors the backend Document model after the tagging refactor.
 */
export interface UserDocument {
  id: number;
  applicationId: number;
  applicationDocumentRequirementId: number;
  uploadedBy: number;
  documentCategory: string;
  fileName: string;
  storedFilePath: string;
  fileSize: number;
  mimeType: string;
  description: string | null;
  slotKey?: string | null;
  status: 'uploaded' | 'under_review' | 'approved' | 'rejected' | 'needs_reupload';
  version: number;
  parentDocumentId: number | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  createdAt: string;
  updatedAt: string;
  /** Provided by GET /:applicationId/documents/:documentId only. */
  downloadUrl?: string;
}

export interface ApplicationRequirementsStatus {
  visaType: string;
  applicationStage: string;
  allRequirementsMet: boolean;
  requirements: RequirementStatus[];
}

/**
 * Get document requirements for a visa type
 */
export const getDocumentRequirements = async (
  visaType: string,
  stage?: string
): Promise<DocumentRequirement[]> => {
  try {
    const url = `/document-requirements/visa-types/${visaType}/requirements${
      stage ? `?stage=${stage}` : ''
    }`;
    const response = await apiClient.get<{ success: boolean; data: DocumentRequirement[] }>(url);
    return response.data.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.error || error?.message || 'Failed to load document requirements';
    console.error('API Error in getDocumentRequirements:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

/**
 * Check application's document requirements status
 */
export const checkApplicationRequirements = async (
  applicationId: number
): Promise<ApplicationRequirementsStatus> => {
  try {
    const response = await apiClient.get<{
      success: boolean;
      data: ApplicationRequirementsStatus;
    }>(`/document-requirements/applications/${applicationId}/check`);
    return response.data.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.error ||
      error?.message ||
      'Failed to check application requirements';
    console.error('API Error in checkApplicationRequirements:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

/**
 * Upload a document for a specific application requirement.
 *
 * After the tagging refactor (backend PR #111), the FK
 * `applicationDocumentRequirementId` is required by the API. Callers should
 * pass the `requirementId` from the `RequirementStatus` card the user
 * uploaded against.
 */
export const uploadDocument = async (
  applicationId: number,
  applicationDocumentRequirementId: number,
  file: File,
  category: string,
  description?: string,
  tags?: string[],
  slotKey?: string
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    formData.append(
      'applicationDocumentRequirementId',
      String(applicationDocumentRequirementId)
    );
    if (description) {
      formData.append('description', description);
    }
    if (tags && tags.length > 0) {
      formData.append('tags', JSON.stringify(tags));
    }
    if (slotKey) {
      formData.append('slotKey', slotKey);
    }

    const response = await apiClient.post(
      `/applications/${applicationId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 413) {
      const friendly = `File is too large or the request was rejected. Try a file under ${MAX_CASE_DOCUMENT_UPLOAD_LABEL}.`;
      console.error('API Error in uploadDocument:', { status, message: error?.message });
      throw new Error(friendly);
    }
    const message = error?.response?.data?.error?.message || error?.message || 'Failed to upload document';
    console.error('API Error in uploadDocument:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

/**
 * Get documents for an application
 */
export const getDocuments = async (
  applicationId: number,
  category?: string,
  status?: string
): Promise<UserDocument[]> => {
  try {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (status) params.append('status', status);
    
    const url = `/applications/${applicationId}/documents${
      params.toString() ? `?${params.toString()}` : ''
    }`;
    
    const response = await apiClient.get<{ success: boolean; data: UserDocument[] }>(url);
    return response.data.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.error?.message || error?.message || 'Failed to load documents';
    console.error('API Error in getDocuments:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

/**
 * Get a single document (includes downloadUrl)
 */
export const getDocument = async (
  applicationId: number,
  documentId: number
): Promise<any> => {
  try {
    const response = await apiClient.get<{ success: boolean; data: any }>(
      `/applications/${applicationId}/documents/${documentId}`
    );
    return response.data.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.error?.message || error?.message || 'Failed to load document';
    console.error('API Error in getDocument:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

/**
 * Download document bytes through the authenticated API (streams from S3/local).
 * Prefer this over fetching a signed S3 URL via apiClient — that causes CORS
 * Network Errors because auth headers are sent cross-origin.
 */
export const downloadDocumentBlob = async (
  applicationId: number,
  documentId: number
): Promise<Blob> => {
  try {
    const response = await apiClient.get(
      `/applications/${applicationId}/documents/${documentId}/download`,
      { responseType: 'blob' }
    );
    return response.data instanceof Blob
      ? response.data
      : new Blob([response.data]);
  } catch (error: any) {
    const message =
      error?.response?.data?.error?.message ||
      error?.message ||
      'Failed to download document';
    console.error('API Error in downloadDocumentBlob:', {
      message: error?.message,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

/**
 * Replace a document. Backend (#112) smart-routes:
 *   - approved docs → creates a new version, preserves the original
 *   - any other status → soft-deletes the current doc, uploads fresh under
 *     the same requirement
 *
 * Returns the resulting document. The card should refetch its list afterwards
 * because the doc id may change (the second path produces a new row).
 */
export const replaceDocument = async (
  applicationId: number,
  documentId: number,
  file: File
): Promise<UserDocument> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<{ success: boolean; data: UserDocument }>(
      `/applications/${applicationId}/documents/${documentId}/replace`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 413) {
      throw new Error(
        `File is too large or the request was rejected. Try a file under ${MAX_CASE_DOCUMENT_UPLOAD_LABEL}.`
      );
    }
    const message =
      error?.response?.data?.error?.message || error?.message || 'Failed to replace document';
    console.error('API Error in replaceDocument:', {
      message: error?.message,
      response: error?.response?.data,
      status,
    });
    throw new Error(message);
  }
};

/**
 * Delete a document
 */
export const deleteDocument = async (
  applicationId: number,
  documentId: number
): Promise<void> => {
  try {
    await apiClient.delete(`/applications/${applicationId}/documents/${documentId}`);
  } catch (error: any) {
    const message =
      error?.response?.data?.error?.message || error?.message || 'Failed to delete document';
    console.error('API Error in deleteDocument:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

/**
 * Get document statistics for an application
 */
export const getDocumentStats = async (applicationId: number): Promise<any> => {
  try {
    const response = await apiClient.get<{ success: boolean; data: any }>(
      `/applications/${applicationId}/documents/stats`
    );
    return response.data.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.error?.message || error?.message || 'Failed to load document statistics';
    console.error('API Error in getDocumentStats:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

/**
 * Cover Letter Interfaces
 */
export interface CoverLetterStatus {
  gate: {
    passed: boolean;
    reason: string | null;
    formStatus: {
      totalForms: number;
      completedForms: number;
      requiredForms: number;
      completedRequiredForms: number;
      isAllFormsCompleted: boolean;
      isAllRequiredFormsCompleted: boolean;
    };
  };
  mainForm: {
    formId: number;
    formNumber: string;
  } | null;
  templateSelection: {
    matchedTemplateId: number;
    matchedTemplateName: string;
  } | null;
}

export interface CoverLetterRender {
  application: {
    id: number;
    visaType: string;
  };
  gate: {
    passed: boolean;
    reason: string | null;
    formStatus: any;
  };
  mainForm: {
    formId: number;
    formNumber: string;
    applicationFormId: number | null;
    status: string;
  };
  templateSelection: {
    matchedTemplateId: number;
    matchedTemplateName: string;
    matchReason: string;
    evaluations: Array<{
      questionId: number;
      operator: string;
      expected: any;
      actual: any;
      passed: boolean;
    }>;
    fallbackUsed: boolean;
  };
  filingAddress: {
    line1: string;
    line2: string;
    line3: string;
    line4: string;
  };
  answers: {
    source: string;
    questionIdToValue: Record<string, any>;
  };
  render: {
    format: string;
    templatePlaceholdersUsed: Array<{
      key: string;
      resolved: boolean;
    }>;
    markdown: string;
  };
}

/**
 * Get cover letter status (gate check + template match info)
 * @param applicationId - Application ID
 */
export const getCoverLetterStatus = async (
  applicationId: number
): Promise<CoverLetterStatus> => {
  try {
    const response = await apiClient.get<{ success: boolean; data: CoverLetterStatus }>(
      `/applications/${applicationId}/cover-letter/status`
    );
    return response.data.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.error?.message || error?.message || 'Failed to load cover letter status';
    console.error('API Error in getCoverLetterStatus:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

/**
 * Get rendered cover letter (returns JSON with rendered markdown + metadata)
 * @param applicationId - Application ID
 */
export const getCoverLetterRender = async (
  applicationId: number
): Promise<CoverLetterRender> => {
  try {
    const response = await apiClient.get<{ success: boolean; data: CoverLetterRender }>(
      `/applications/${applicationId}/cover-letter/render`
    );
    return response.data.data;
  } catch (error: any) {
    // Handle gate failure
    if (error?.response?.status === 409 && error?.response?.data?.code === 'FORMS_NOT_COMPLETED') {
      const formStatus = error?.response?.data?.data?.formStatus;
      const customError = new Error(error?.response?.data?.message || 'Forms not completed');
      (customError as any).code = 'FORMS_NOT_COMPLETED';
      (customError as any).formStatus = formStatus;
      throw customError;
    }

    // Handle template not found
    if (error?.response?.status === 404 && error?.response?.data?.code === 'NO_TEMPLATE_MATCH') {
      const customError = new Error(error?.response?.data?.message || 'No template match');
      (customError as any).code = 'NO_TEMPLATE_MATCH';
      throw customError;
    }

    const message =
      error?.response?.data?.error?.message || error?.message || 'Failed to render cover letter';
    console.error('API Error in getCoverLetterRender:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};